import React, {
  useCallback,
} from 'react';
import clsx from 'clsx';
import noop from 'lodash/noop';
import {
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Tooltip from 'core/components/Tooltip';
import {
  useNotify,
} from 'core/hooks';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import Input from 'pages/Dashboard/components/Input';
import {
  PatientPaymentTransactionViewModelDTO,
} from 'dtos';
import {
  useSendPatientReceipt,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  validateEmail,
} from 'pages/Dashboard/utils/helper';

type ReceiptEmailForm = {
  email?: string;
};

type Props = {
  payment: PatientPaymentTransactionViewModelDTO;
  onClose: () => void;
};

function getPatientEmail(payment: PatientPaymentTransactionViewModelDTO) {
  const { patient } = payment;
  const {
    isBillingContact,
    email: patientContactEmail,
  } = patient?.otherContact ?? {};

  return (isBillingContact ? patientContactEmail : patient?.email) ?? '';
}

export default function ReceiptEmailDialog({ payment, onClose }: Props) {
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<ReceiptEmailForm>({
    mode: 'all',
    defaultValues: { email: getPatientEmail(payment) },
  });
  const notify = useNotify();
  const {
    isPending: isSending,
    mutateAsync: sendReceipt,
  } = useSendPatientReceipt();

  const onSubmit = useCallback(async ({ email }: ReceiptEmailForm) => {
    if (payment.patientPaymentTransactionId == null || payment.type == null) {
      return;
    }

    try {
      await sendReceipt({
        patientPaymentTransactionId: payment.patientPaymentTransactionId,
        type: payment.type,
        email,
      });
      notify({
        message: 'Receipt sent successfully!',
        variant: 'success',
      });
      onClose();
    } catch {
      // ReactQueryProvider displays the mutation error and keeps this dialog open for retry.
    }
  }, [notify, onClose, payment.patientPaymentTransactionId, payment.type, sendReceipt]);

  return (
    <Dialog
      open
      title="Email Receipt"
      onClose={onClose}
      onConfirm={noop}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="md"
      className="populate-form"
    >
      <div className="flex flex-col gap-form-row">
        <Controller
          control={control}
          name="email"
          rules={{ required: true, validate: validateEmail }}
          render={({ field, fieldState: { invalid } }) => (
            <Input
              {...field}
              required
              id="receipt-email"
              error={invalid}
              showErrorText={false}
              className="w-full"
              label="Email"
              placeholder="Enter Email"
            />
          )}
        />
      </div>
      <div className="flex gap-4 justify-end">
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Close
        </Button>
        <Tooltip
          title="Email is missing"
          hide={isValid}
        >
          <div className={clsx('h-full', { 'cursor-not-allowed': !isValid })}>
            <Button
              variant="contained"
              type="button"
              color="primary"
              className="!h-full"
              disabled={!isValid || isSending}
              onClick={handleSubmit(onSubmit)}
            >
              {isSending ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </Tooltip>
      </div>
    </Dialog>
  );
}
